<template>
  <div class="bg-grey-lighten-4 min-h-screen">
    <v-progress-linear v-if="loading" indeterminate color="primary" absolute top style="z-index: 100" />
  
    <div v-if="mobile" class="d-flex flex-column h-100" style="overflow: hidden">
      <v-main style="flex: 1 1 auto; overflow-y: auto;">
        <v-container class="pa-4">
          <div class="d-flex align-center justify-space-between mb-4">
            <div>
              <h2 class="text-h6 font-weight-bold text-indigo-darken-4">MF Dashboard</h2>
              <div class="text-caption text-medium-emphasis">
                {{ portfolioHoldings.length }} Active Schemes
              </div>
            </div>
          </div>
          <v-card class="mb-4 rounded-xl mx-auto elevation-2" color="indigo-darken-4" theme="dark" @click="goToMfDetails"
            style="cursor: pointer">
            <v-card-text class="text-center pa-6">
              <div class="d-flex align-center justify-center mb-1">
                <div class="text-subtitle-2 text-grey-lighten-2">Total Portfolio Value</div>
                <v-btn 
                  icon="mdi-bucket-outline" 
                  variant="text" 
                  size="small" 
                  class="ml-1" 
                  color="grey-lighten-2" 
                  @click.stop="goToMfBucket" 
                  title="View Bucket Strategy">
                </v-btn>
              </div>
              <div class="text-h3 font-weight-bold text-white">
                {{ formatINR(totalPortfolioValue) }}
              </div>
            </v-card-text>
          </v-card>
  
          <v-card class="rounded-lg mx-auto border-thin" elevation="0">
            <v-card-title class="text-subtitle-2 font-weight-bold py-3 px-4 bg-grey-lighten-5 border-b">
              Holdings List
            </v-card-title>
  
            <v-data-table :headers="mobileHeaders" :items="portfolioHoldings" :items-per-page="-1" class="elevation-0"
              hide-default-footer mobile-breakpoint="0">
              <template v-slot:[`item.schemeName`]="{ item }">
                <div class="py-3">
                  <div class="font-weight-medium text-body-2 text-truncate" style="max-width: 180px;">
                    {{ item.schemeName }}
                  </div>
                  <div class="text-caption text-indigo mt-1 font-weight-bold" v-if="item.accountName">
                    {{ item.accountName }}
                  </div>
                  <div class="text-caption text-grey mt-1">
                    {{ item.quantity }} units @ {{ formatINR(item.currentNav) }}
                  </div>
                </div>
              </template>
              <template v-slot:[`item.fundCategory`]="{ item }">
                <v-chip
                  size="x-small"
                  color="indigo-lighten-4"
                  class="text-caption font-weight-bold"
                  variant="flat"
                >
                  {{ item.fundCategory }}
                </v-chip>
              </template>
              <template v-slot:[`item.currentValue`]="{ item }">
                <div class="font-weight-bold text-body-1 text-green-darken-2 text-right">
                  {{ formatINR(item.currentValue) }}
                </div>
              </template>
              <template v-slot:[`item.bucketType`]="{ item }">
              <div class="d-flex align-center">
                <span class="font-weight-medium text-body-2 mr-2">
                  {{ item.bucketType || 'No Bucket' }}
                </span>
                <v-btn 
                  icon="mdi-pencil" 
                  variant="text" 
                  size="x-small" 
                  color="grey"
                  @click="openBucketEditDialog(item)"
                ></v-btn>
              </div>
            </template>
              <template v-slot:no-data>
                <div class="pa-4 text-center text-grey">No funds found</div>
              </template>
            </v-data-table>
          </v-card>
        </v-container>
      </v-main>
    </div>
  
    <v-container v-else fluid class="pa-4">
  
      <v-card class="mb-6 rounded-xl mx-auto" max-width="1200" elevation="2">
        <v-card-text class="text-center pa-8">
          <h2 class="text-h5 font-weight-bold text-indigo-darken-4 mb-6">
            My Mutual Fund Portfolio
          </h2>
          <v-row justify="center">
            <v-col cols="12" class="text-center">
              <div class="d-flex align-center justify-center mb-2">
                <div class="text-subtitle-1 text-medium-emphasis">Total Portfolio Value</div>
                <v-btn 
                  icon="mdi-bucket-outline" 
                  variant="text" 
                  size="small" 
                  class="ml-2" 
                  color="indigo-darken-4" 
                  @click.stop="goToMfBucket" 
                  title="View Bucket Strategy">
                </v-btn>
              </div>
              <div class="text-h3 font-weight-bold text-green-darken-1" @click="goToMfDetails" style="cursor: pointer">
                {{ formatINR(totalPortfolioValue) }}
              </div>
            </v-col>
          </v-row>
          <div class="d-flex justify-end mt-6">
            <v-btn color="primary" variant="flat" prepend-icon="mdi-refresh" @click="refreshPortfolio" :loading="loading">
              Refresh Latest NAV
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
  
      <v-card class="mb-6 rounded-lg mx-auto" max-width="1200" elevation="1">
        <v-card-text class="pa-6 bg-grey-lighten-5">
          <h3 class="text-h6 font-weight-bold mb-4">Add Transaction</h3>
          <v-row>
            <v-col cols="12" md="4">
              <v-autocomplete 
                v-model="newTransaction.fund" 
                v-model:search="searchQuery" 
                :items="fundSearchResults"
                item-title="schemeName" 
                item-value="isin" 
                label="Search for fund name..." 
                prepend-inner-icon="mdi-magnify"
                variant="outlined" 
                density="comfortable" 
                hide-details 
                bg-color="white" 
                clearable 
                return-object
                :loading="searchLoading" 
                no-filter 
                @update:search="onSearchInput"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="newTransaction.bucket"
                :items="['BUCKET1', 'BUCKET2', 'BUCKET3']"
                label="Bucket"
                variant="outlined"
                density="comfortable"
                hide-details
                bg-color="white"
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field 
                v-model.number="newTransaction.units" 
                label="Units" 
                type="number" 
                variant="outlined"
                density="comfortable" 
                hide-details 
                bg-color="white"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="newTransaction.userMfAccountId"
                :items="mfAccounts"
                item-title="userMfAccountName"
                item-value="userMfAccountId"
                label="Select Account"
                variant="outlined"
                density="comfortable"
                hide-details
                bg-color="white"
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn 
                color="success" 
                variant="flat" 
                block 
                height="48" 
                @click="addTransaction"
                :disabled="!newTransaction.fund || newTransaction.units <= 0 || !newTransaction.userMfAccountId"
              >
                Add Fund
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
  
      <v-card class="rounded-lg mx-auto" max-width="1200" elevation="1">
        <v-data-table :headers="webHeaders" :items="portfolioHoldings" :items-per-page="-1" class="elevation-0"
          hide-default-footer hover>
          <template v-slot:[`item.bucketType`]="{ item }">
            <div class="d-flex align-center">
              <span class="font-weight-medium text-body-2 mr-2">
                {{ item.bucketType || 'No Bucket' }}
              </span>
              <v-btn 
                icon="mdi-pencil" 
                variant="text" 
                size="x-small" 
                color="grey"
                @click="openBucketEditDialog(item)"
              ></v-btn>
            </div>
          </template>
          <template v-slot:[`item.accountName`]="{ item }">
            <div class="d-flex align-center">
              <span class="font-weight-medium text-body-2 mr-2">
                {{ item.accountName || 'No Account' }}
              </span>
              <v-btn icon="mdi-pencil" variant="text" size="x-small" color="grey"
                @click="openAccountEditDialog(item)"></v-btn>
            </div>
          </template>
  
          <template v-slot:[`item.currentNav`]="{ item }">
            {{ formatINR(item.currentNav) }}
          </template>
          <template v-slot:[`item.currentValue`]="{ item }">
            <span class="font-weight-bold text-grey-darken-3">
              {{ formatINR(item.currentValue) }}
            </span>
          </template>
          <template v-slot:[`item.action`]="{ item }">
            <div class="d-flex justify-end gap-2">
              <v-btn color="error" variant="outlined" size="small" class="mr-2" @click="removeFund(item)">
                Remove
              </v-btn>
              <v-btn color="warning" variant="outlined" size="small" @click="openReduceDialog(item)">
                Reduce
              </v-btn>
            </div>
          </template>
          <template v-slot:[`body.append`]>
            <tr class="bg-green-lighten-5">
              <td class="font-weight-bold text-subtitle-1 ps-4" colspan="2">Grand Total</td>
              <td colspan="2"></td>
              <td class="font-weight-bold text-subtitle-1">
                {{ formatINR(totalPortfolioValue) }}
              </td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  
    <v-dialog v-model="reduceDialog.show" max-width="400">
      <v-card class="rounded-lg pa-4">
        <v-card-title class="text-h6 pb-0">Reduce Quantity</v-card-title>
        <v-card-subtitle class="mt-1">{{ reduceDialog.item?.schemeName }}</v-card-subtitle>
        <v-card-text class="pt-4">
          <v-text-field v-model.number="reduceDialog.units" label="Units to Sell" type="number" variant="outlined"
            density="comfortable" hide-details autofocus
            :rules="[v => v > 0 || 'Must be positive', v => v <= (reduceDialog.item?.quantity || 0) || 'Exceeds balance']"></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end pt-0">
          <v-btn color="grey-darken-1" variant="text" @click="reduceDialog.show = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="confirmReduceQuantity">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  
    <v-dialog v-model="accountDialog.show" max-width="450">
      <v-card class="rounded-lg pa-4">
        <v-card-title class="text-h6 pb-0">Update Account Name</v-card-title>
        <v-card-subtitle class="mt-1">For {{ accountDialog.item?.schemeName }}</v-card-subtitle>
  
        <v-card-text class="pt-4">
          <v-combobox v-model="accountDialog.selectedName" :items="availableAccountNames"
            label="Select or Type Account Name" variant="outlined" density="comfortable" hide-details autofocus
            color="primary" placeholder="e.g. Zerodha, Groww, Family"></v-combobox>
        </v-card-text>
  
        <v-card-actions class="justify-end pt-0">
          <v-btn color="grey-darken-1" variant="text" @click="accountDialog.show = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="saveAccountName" :loading="accountDialog.loading"
            :disabled="!accountDialog.selectedName">
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-dialog v-model="bucketDialog.show" max-width="450">
      <v-card class="rounded-lg pa-4">
        <v-card-title class="text-h6 pb-0">Update Fund Bucket</v-card-title>
        <v-card-subtitle class="mt-1">For {{ bucketDialog.item?.schemeName }}</v-card-subtitle>

        <v-card-text class="pt-4">
          <v-select
            v-model="bucketDialog.selectedBucket"
            :items="['BUCKET1', 'BUCKET2', 'BUCKET3']"
            label="Choose Bucket"
            variant="outlined"
            density="comfortable"
            hide-details
            color="primary"
          ></v-select>
        </v-card-text>

        <v-card-actions class="justify-end pt-0">
          <v-btn color="grey-darken-1" variant="text" @click="bucketDialog.show = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            variant="flat" 
            @click="saveBucketUpdate" 
            :loading="bucketDialog.loading"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useDisplay } from 'vuetify'
import { useRouter } from 'vue-router'

// --- FIX: Suppress ResizeObserver Loop Error ---
const loopError = 'ResizeObserver loop completed with undelivered notifications'
const _error = console.error
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes(loopError)) return
  _error(...args)
}

const store = useStore()
const { mobile } = useDisplay()
const router = useRouter()
const loading = ref(false)
const searchLoading = ref(false)

// --- Search State ---
const searchQuery = ref('')
const fundSearchResults = ref([])
const searchTimeout = ref(null)

// --- Transaction & Dialog State ---
const newTransaction = ref({ 
  fund: null, 
  units: 0, 
  userMfAccountId: null,
  bucket: 'BUCKET1'
})
const reduceDialog = ref({ show: false, units: 0, item: null })

const accountDialog = ref({
  show: false,
  loading: false,
  item: null,
  selectedName: ''
})
const availableAccountNames = ref([])

const portfolioHoldings = ref([])

// --- Headers Configuration ---
const webHeaders = [
  { title: 'Account Name', key: 'accountName', align: 'start', width: '20%' },
  { title: 'MF Name', key: 'schemeName', align: 'start', width: '30%' },
  { title: 'Fund Type', key: 'fundCategory', align: 'start', width: '15%' },
  { title: 'Bucket', key: 'bucketType', align: 'start', width: '15%' },
  { title: 'Units', key: 'quantity', align: 'end' },
  { title: 'Current NAV (₹)', key: 'currentNav', align: 'end' },
  { title: 'Current Value (₹)', key: 'currentValue', align: 'end' },
  { title: 'Action', key: 'action', align: 'end', sortable: false }
]
const bucketDialog = ref({
  show: false,
  loading: false,
  item: null,
  selectedBucket: ''
})

const mobileHeaders = [
  { title: 'Fund Name', key: 'schemeName', align: 'start', width: '60%' },
  { title: 'Current Value', key: 'currentValue', align: 'end', width: '40%' },
]

const totalPortfolioValue = computed(() => {
  return portfolioHoldings.value.reduce((sum, item) => sum + (item.currentValue || 0), 0)
})

// --- Routing Additions ---
const goToMfDetails = () => {
  router.push('/mf/details')
}

// NEW FUNCTION: Added routing function for Bucket Strategy View
const goToMfBucket = () => {
  router.push('/mf/bucket') // adjust route path if necessary based on your router config
}

// --- Search Logic ---
const onSearchInput = (val) => {
  if (!val || typeof val === 'object') return
  if (searchTimeout.value) clearTimeout(searchTimeout.value)
  searchLoading.value = true
  searchTimeout.value = setTimeout(async () => {
    try {
      const response = await store.dispatch('portfolio/getMfMasterList', { schemeName: val })
      fundSearchResults.value = response?.mfMasterList || []
    } catch (err) {
      console.error('Failed to search funds:', err)
      fundSearchResults.value = []
    } finally {
      searchLoading.value = false
    }
  }, 500)
}

// --- API Actions ---
const refreshPortfolio = async () => {
  loading.value = true
  try {
    const res = await store.dispatch('portfolio/refreshMaster')
    if (res && res.mfPortoflioList) {
      portfolioHoldings.value = res.mfPortoflioList.map(item => ({
        ...item,
        currentNav: item.nav,
        currentValue: item.quantity * item.nav,
        accountName: item.accountName,
        fundCategory: item.fundCategory,
        schemeCode: item.schemeCode
      }))
    }
  } catch (err) {
    console.error("Failed to fetch portfolio", err)
  } finally {
    loading.value = false
  }
}

const fetchPortfolio = async () => {
  loading.value = true
  try {
    const res = await store.dispatch('portfolio/getMfPortfolio')
    if (res && res.mfPortoflioList) {
      portfolioHoldings.value = res.mfPortoflioList.map(item => ({
        ...item,
        currentNav: item.nav,
        currentValue: item.quantity * item.nav,
        accountName: item.accountName,
        fundCategory: item.fundCategory ,
        schemeCode: item.schemeCode
      }))
    }
  } catch (err) {
    console.error("Failed to fetch portfolio", err)
  } finally {
    loading.value = false
  }
}

const openAccountEditDialog = async (item) => {
  accountDialog.value = {
    show: true,
    loading: false,
    item: { ...item },
    selectedName: item.accountName || ''
  }

  try {
    const res = await store.dispatch('portfolio/getMyMfAccountNameList')
    if (res && Array.isArray(res.accountNameList)) {
      availableAccountNames.value = res.accountNameList
    } else if (Array.isArray(res)) {
      availableAccountNames.value = res
    } else {
      availableAccountNames.value = []
    }
  } catch (err) {
    console.error("Failed to fetch account names", err)
    availableAccountNames.value = []
  }
}

const saveAccountName = async () => {
  if (!accountDialog.value.item || !accountDialog.value.selectedName) return

  accountDialog.value.loading = true
  try {
    await store.dispatch('portfolio/updateUserMfAccountName', {
      isin: accountDialog.value.item.isin,
      accountName: accountDialog.value.selectedName
    })

    store.commit('snackbar/showMessage', { text: 'Account name updated successfully', color: 'success' })
    accountDialog.value.show = false
    await fetchPortfolio() 
  } catch (err) {
    console.error(err)
    store.commit('snackbar/showMessage', { text: 'Failed to update account name', color: 'error' })
  } finally {
    accountDialog.value.loading = false
  }
}

const mfAccounts = ref([])

const fetchMfAccounts = async () => {
  try {
    const res = await store.dispatch('portfolio/getMyMfAccountDataList')
    if (res && res.userMfAccountDataList) {
      mfAccounts.value = res.userMfAccountDataList
    } else if (Array.isArray(res)) {
      mfAccounts.value = res
    } else {
      mfAccounts.value = []
    }
  } catch (err) {
    console.error("Failed to fetch MF accounts", err)
    mfAccounts.value = []
  }
}

const addTransaction = async () => {
  // Add a typeof check to ensure they selected a valid object from the list, not just a string
  if (
    !newTransaction.value.fund || 
    typeof newTransaction.value.fund !== 'object' || 
    newTransaction.value.units <= 0 || 
    !newTransaction.value.userMfAccountId
  ) {
    store.commit('snackbar/showMessage', { text: 'Please select a valid fund from the dropdown', color: 'error' })
    return
  }
  
  loading.value = true
  try {
    await store.dispatch('portfolio/addMfToPortfolio', {
      isin: newTransaction.value.fund.isin || '',
      schemeCode: newTransaction.value.fund.schemeCode, // This will now safely exist
      quantity: newTransaction.value.units,
      userMfAccountId: newTransaction.value.userMfAccountId,
      bucketType: newTransaction.value.bucket // Changed from 'bucket' to 'bucketType'
    })
    
    store.commit('snackbar/showMessage', { text: 'Transaction added successfully', color: 'success' })
    
    newTransaction.value = { 
      fund: null, 
      units: 0, 
      userMfAccountId: null ,
      bucket: 'BUCKET1'
    }
    
    searchQuery.value = ''
    await fetchPortfolio()
  } catch (err) {
    store.commit('snackbar/showMessage', { text: 'Failed to add transaction', color: 'error' })
  } finally {
    loading.value = false
  }
}

const openBucketEditDialog = (item) => {
  bucketDialog.value = {
    show: true,
    loading: false,
    item: item,
    selectedBucket: item.bucket || 'BUCKET1'
  }
}

const saveBucketUpdate = async () => {
  bucketDialog.value.loading = true
  try {
    await store.dispatch('portfolio/updateFundBucket', {
      schemeCode: bucketDialog.value.item.schemeCode,
      bucket: bucketDialog.value.selectedBucket
    })
    store.commit('snackbar/showMessage', { text: 'Bucket updated successfully', color: 'success' })
    bucketDialog.value.show = false
    await fetchPortfolio() 
  } catch (err) {
    store.commit('snackbar/showMessage', { text: 'Failed to update bucket', color: 'error' })
  } finally {
    bucketDialog.value.loading = false
  }
}

const removeFund = async (item) => {
  if (!confirm(`Are you sure you want to remove ${item.schemeName}?`)) return
  loading.value = true
  try {
    await store.dispatch('portfolio/removeMfFromPortfolio', { isin: item.isin })
    store.commit('snackbar/showMessage', { text: 'Fund removed successfully', color: 'success' })
    await fetchPortfolio()
  } catch (err) {
    store.commit('snackbar/showMessage', { text: 'Failed to remove fund', color: 'error' })
  } finally {
    loading.value = false
  }
}

const openReduceDialog = (item) => {
  reduceDialog.value = { show: true, units: 0, item: { ...item } }
}

const confirmReduceQuantity = async () => {
  const { item, units } = reduceDialog.value
  loading.value = true
  try {
    await store.dispatch('portfolio/redeuceMfFromPortfolio', { isin: item.isin, quantity: units })
    store.commit('snackbar/showMessage', { text: 'Quantity reduced successfully', color: 'success' })
    reduceDialog.value.show = false
    await fetchPortfolio()
  } catch (err) {
    store.commit('snackbar/showMessage', { text: 'Failed to reduce quantity', color: 'error' })
  } finally {
    loading.value = false
  }
}

const formatINR = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num || 0)

onMounted(() => {
  fetchPortfolio()
  fetchMfAccounts()
})
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}
</style>